<template>
  <!-- 视频详情弹窗 —— My Videos 页与创作页结果流共用 -->
  <Teleport to="body">
    <div class="detail-overlay" @click="$emit('close')">
      <div class="detail-dialog" @click.stop>
        <div class="detail-head">
          <h2>Video Details</h2>
          <div class="detail-head-right">
            <!-- fetch→blob→a[download] 真下载（跨域 download 属性会被忽略而跳转 CDN） -->
            <button
              v-if="video.taskStatus === 'COMPLETED' && video.resultUrl"
              type="button"
              class="download-btn"
              :disabled="downloading"
              @click="handleDownload"
            >
              <span v-if="downloading" class="dl-spin" />
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              {{ downloading ? 'Preparing…' : 'Download' }}
            </button>
            <button type="button" class="close-btn" aria-label="Close" @click="$emit('close')">✕</button>
          </div>
        </div>

        <div class="detail-body">
          <video
            v-if="video.taskStatus === 'COMPLETED' && video.resultUrl"
            :src="video.resultUrl"
            class="detail-video"
            controls
            preload="metadata"
            playsinline
          />
          <div v-if="video.taskStatus === 'FAILED' && video.errorMessage" class="detail-error">
            {{ video.errorMessage }}
          </div>

          <div class="detail-row">
            <span class="k">Created</span>
            <span class="v">{{ formatDateTime(video.taskCreatedAt) }}</span>
          </div>

          <template v-if="video.params?.prompt">
            <div class="detail-section-row">
              <h3 class="detail-section">Prompt</h3>
              <CopyPromptButton :text="video.params.prompt" />
            </div>
            <!-- @image1 token 渲染成「缩略图 + token + 底色」chip（展示层，存储不动） -->
            <MentionPromptText
              class="detail-prompt"
              :prompt="video.params.prompt"
              :image-urls="video.params?.imageUrls ?? []"
              :video-urls="video.params?.videoUrls ?? []"
            />
          </template>

          <h3 class="detail-section">Details</h3>
          <div class="detail-rows">
            <div class="detail-row">
              <span class="k">Model</span>
              <span class="v">{{ seedanceModelLabel(video.toolId) }}</span>
            </div>
            <div class="detail-row">
              <span class="k">Type</span>
              <span class="v">Video</span>
            </div>
            <div class="detail-row">
              <span class="k">Order ID</span>
              <span class="v">{{ video.orderId }}</span>
            </div>
            <div v-if="video.totalCredits != null" class="detail-row">
              <span class="k">Credits</span>
              <span class="v">{{ video.totalCredits }}</span>
            </div>
          </div>

          <template v-if="hasReferences">
            <h3 class="detail-section">References</h3>
            <!-- 统一方形块 + @token 角标，点击盖层放大预览 -->
            <ReferenceAssetsGrid
              :image-urls="video.params?.imageUrls ?? []"
              :video-urls="video.params?.videoUrls ?? []"
              :audio-urls="video.params?.audioUrls ?? []"
            />
          </template>

          <h3 class="detail-section">Settings</h3>
          <div class="detail-rows">
            <div class="detail-row">
              <span class="k">Aspect Ratio</span>
              <span class="v">{{ video.params?.aspectRatio ?? '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="k">Duration</span>
              <span class="v">{{ video.params?.duration != null ? `${video.params.duration}s` : '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="k">Resolution</span>
              <span class="v">{{ video.params?.resolution ?? '—' }}</span>
            </div>
            <div class="detail-row">
              <span class="k">Bitrate</span>
              <span class="v">{{ video.params?.bitrate_mode === 'high' ? 'High' : 'Standard' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import CopyPromptButton from '~/components/seedance/CopyPromptButton.vue';
import MentionPromptText from '~/components/seedance/MentionPromptText.vue';
import ReferenceAssetsGrid from '~/components/seedance/ReferenceAssetsGrid.vue';
import { downloadMediaFile } from '~/utils/seedanceMedia';

const props = defineProps<{ video: any }>();
defineEmits<{ (e: 'close'): void }>();

// 真下载：fetch→blob→a[download]，期间按钮转 loading，CORS 拒绝退回新标签页
const downloading = ref(false);
const handleDownload = async () => {
  if (downloading.value || !props.video?.resultUrl) return;
  downloading.value = true;
  try {
    await downloadMediaFile(props.video.resultUrl, `seedance-${props.video.orderId ?? 'video'}.mp4`);
  } finally {
    downloading.value = false;
  }
};

// 详情弹窗时间格式："Jul 13, 2026 · 08:32"
const formatDateTime = (d: string | undefined) => {
  if (!d) return '—';
  const date = new Date(d);
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} · ${time}`;
};

// 模型值（存于 order.toolId）→ 展示名。覆盖 2.0 / Fast / Mini / 2.5，
// 未知或历史 2.0 记录一律回退 "Seedance 2.0"。
const seedanceModelLabel = (model: unknown): string => {
  switch (model) {
    case 'minimax_h3':
      return 'MiniMax H3';
    case 'seedance_2_5':
      return 'Seedance 2.5';
    case 'seedance_2_0_fast':
      return 'Seedance 2.0 Fast';
    case 'seedance_2_0_mini':
      return 'Seedance 2.0 Mini';
    default:
      return 'Seedance 2.0';
  }
};

const hasReferences = computed(() => {
  const p = props.video?.params ?? {};
  return (p.imageUrls?.length ?? 0) > 0 || (p.videoUrls?.length ?? 0) > 0 || (p.audioUrls?.length ?? 0) > 0;
});
</script>

<style scoped>
.detail-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.detail-dialog {
  display: flex; flex-direction: column;
  width: 100%; max-width: 42rem; max-height: 85vh;
  background: #131313;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  color: white;
}
.detail-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.detail-head h2 { margin: 0; font-size: 1rem; font-weight: 700; }
.detail-head-right { display: flex; align-items: center; gap: 0.5rem; }
.download-btn {
  display: inline-flex; align-items: center; gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  cursor: pointer;
  text-decoration: none;
}
.download-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); color: white; }
.download-btn:disabled { opacity: 0.6; cursor: wait; }
.dl-spin {
  width: 12px; height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-top-color: white;
  animation: dl-spin 1s linear infinite;
  display: inline-block;
}
@keyframes dl-spin { to { transform: rotate(360deg); } }
.close-btn {
  background: transparent; border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem; cursor: pointer;
}
.close-btn:hover { color: white; }
.detail-body { overflow-y: auto; padding: 1rem 1.25rem 1.25rem; }
.detail-video {
  width: 100%; border-radius: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: black;
  margin-bottom: 1rem;
}
.detail-error {
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  background: rgba(239, 68, 68, 0.1);
  color: #f87171; font-size: 0.75rem;
}
.detail-section {
  margin: 1rem 0 0.25rem;
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
}
/* Prompt 标题 + 一键复制按钮同行 */
.detail-section-row {
  display: flex; align-items: center; justify-content: space-between;
  margin: 1rem 0 0.25rem;
}
.detail-section-row .detail-section { margin: 0; }
.detail-prompt {
  max-height: 8rem; overflow-y: auto;
  white-space: pre-wrap;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
}
.detail-rows > .detail-row + .detail-row { border-top: 1px solid rgba(255, 255, 255, 0.06); }
.detail-row {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
  padding: 0.4rem 0;
}
.detail-row .k { flex-shrink: 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); }
.detail-row .v { font-size: 0.75rem; color: rgba(255, 255, 255, 0.9); text-align: right; word-break: break-all; }
</style>
